import { ArrowRight } from "lucide-react";
import { LenderLogo } from "./LenderLogo";
import { type Lender } from "@/lib/lenders";
import { useApplyUrl } from "@/lib/epi";

function formatKr(n: number) {
  return new Intl.NumberFormat("sv-SE").format(n) + " kr";
}

export function FeaturedBank({
  lender,
  title,
  highlightWord,
  compact = false,
}: {
  lender: Lender;
  title?: string;
  highlightWord?: string;
  compact?: boolean;
}) {
  const apply = useApplyUrl();
  const parts =
    title && highlightWord && title.includes(highlightWord)
      ? title.split(highlightWord)
      : null;
  return (
    <div
      className={`relative w-full rounded-md border border-border bg-card p-5 shadow-elevated sm:p-6 ${
        compact ? "" : "mx-auto max-w-md"
      }`}
    >
      <div className="absolute -left-3 top-4 rounded-md bg-primary px-3 py-1 text-[10px] font-black uppercase tracking-wider text-primary-foreground shadow-md sm:text-[11px]">
        {lender.badge ?? "MEST POPULÄR"}
      </div>

      {title && (
        <h3 className="mb-3 mt-4 text-center font-display text-2xl font-extrabold leading-tight tracking-tight text-foreground sm:text-[26px]">
          {parts ? (
            <>
              {parts[0]}
              <span className="underline decoration-primary decoration-[3px] underline-offset-4">
                {highlightWord}
              </span>
              {parts.slice(1).join(highlightWord!)}
            </>
          ) : (
            title
          )}
        </h3>
      )}

      <div className="mx-auto flex w-1/2 items-center justify-center py-1">
        <LenderLogo lender={lender} size={200} bare className="!h-auto w-full" />
      </div>

      <dl className="mt-2 divide-y divide-border border-y border-border">
        <div className="flex items-center justify-between py-2.5">
          <dt className="text-[14px] font-medium text-foreground">Belopp</dt>
          <dd className="font-display text-[14px] font-extrabold text-foreground">
            {formatKr(lender.minAmount)} – {formatKr(lender.maxAmount)}
          </dd>
        </div>
        <div className="flex items-center justify-between py-2.5">
          <dt className="text-[14px] font-medium text-foreground">Löptid</dt>
          <dd className="font-display text-[14px] font-extrabold text-foreground">{lender.term}</dd>
        </div>
        <div className="flex items-center justify-between py-2.5">
          <dt className="text-[14px] font-medium text-foreground">Ränta</dt>
          <dd className="font-display text-[14px] font-extrabold text-primary">{lender.rate}</dd>
        </div>
      </dl>

      <p className="mt-3 text-[13px] leading-relaxed text-foreground/90">
        {lender.description}
        {lender.descriptionHighlight && (
          <>
            {" "}
            <span className="font-semibold text-primary underline underline-offset-2">
              {lender.descriptionHighlight}
            </span>
          </>
        )}
      </p>

      <a
        href={apply(lender, "featured-cta")}
        target="_blank"
        rel="noopener noreferrer sponsored"
        aria-label={`Ansök nu hos ${lender.name}`}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-cta py-3.5 text-base font-bold !text-cta-foreground !no-underline shadow-[0_10px_30px_-10px_color-mix(in_oklab,var(--cta)_60%,transparent)] transition-all hover:brightness-105"
      >
        Ansök nu <ArrowRight className="h-5 w-5" />
      </a>
    </div>
  );
}
